var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useFormContext } from 'react-hook-form';
import { GiveawaySchedulerConfigStatus } from '@daredrop-com/rewards-api-contract';
import { useCreateGiveawayForm } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/hooks/useCreateGiveawayForm';
import { CreateGiveawayFormContext, useCreateGiveawayFormContext, } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/contexts/createGiveawayForm.context';
import { RedesignTimeDurationField } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/RedesignTimeDurationField';
import { RedesignGiveawayAdvancedSettings } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/RedesignGiveawayAdvancedSettings';
import { RedesignSchedulePreview } from 'root/src/client/v2/giveaways/features/CreateGiveawayModal/components/RedesignSchedulePreview';
import { Button } from 'root/src/client/v2/common/components/ui/button';
// Fresh redesign layout for the giveaway form: an animated segmented duration picker and animated
// advanced settings (redesign switches / steppers), with redesign submit buttons. Reuses the same
// form logic/schema (useCreateGiveawayForm). Same props as the legacy CreateGiveawayForm.
export const RedesignCreateGiveawayForm = memo((props) => {
    var _a;
    const { onSubmit, onCancel, giveaway, scheduler, giveawayConfig } = props;
    const submitMutation = useMutation({
        mutationKey: ['giveaway', 'submit'],
        mutationFn: (formData) => __awaiter(void 0, void 0, void 0, function* () {
            return onSubmit(Object.assign(Object.assign({}, formData), { repeatTimes: formData.isRepeatingEnabled ? formData.repeatTimes : null, maxDailyUserWins: formData.isMaxDailyWinsEnabled ? formData.maxDailyUserWins : null }));
        }),
    });
    const form = useCreateGiveawayForm({
        defaultValues: Object.assign(Object.assign({}, scheduler), { isRepeatingEnabled: Boolean(scheduler === null || scheduler === void 0 ? void 0 : scheduler.repeatTimes), isMaxDailyWinsEnabled: Boolean(giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.maxDailyUserWins), maxWinners: giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.maxWinners, maxDailyUserWins: giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.maxDailyUserWins, isTwitchSubscribersOnly: giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.isTwitchSubscribersOnly, voucherCountPerWinner: (_a = giveawayConfig === null || giveawayConfig === void 0 ? void 0 : giveawayConfig.voucherCountPerWinner) !== null && _a !== void 0 ? _a : undefined }),
    });
    const contextValue = useMemo(() => ({ onCancel, giveaway, scheduler }), [onCancel, giveaway, scheduler]);
    return (<CreateGiveawayFormContext.Provider value={contextValue}>
			<FormProvider {...form}>
				<form onSubmit={form.handleSubmit(data => submitMutation.mutateAsync(data))} className="flex w-full flex-col gap-5">
					<RedesignTimeDurationField />
					<RedesignGiveawayAdvancedSettings />
					<RedesignSchedulePreview />
					<RedesignGiveawayButtons />
					{giveaway && (<p className="text-center text-xs text-muted-foreground">Changes take effect in the next giveaway. A running giveaway can be canceled anytime.</p>)}
				</form>
			</FormProvider>
		</CreateGiveawayFormContext.Provider>);
});
const RedesignGiveawayButtons = memo(() => {
    const form = useFormContext();
    const { giveaway, scheduler, onCancel } = useCreateGiveawayFormContext();
    const submitting = form.formState.isSubmitting;
    const cancelMutation = useMutation({
        mutationKey: ['giveaway', 'cancel'],
        mutationFn: () => __awaiter(void 0, void 0, void 0, function* () {
            if (onCancel) {
                yield onCancel();
            }
        }),
    });
    if ((scheduler === null || scheduler === void 0 ? void 0 : scheduler.status) === GiveawaySchedulerConfigStatus.Active) {
        return (<div className="flex w-full flex-col gap-3 sm:flex-row">
				<Button type="submit" variant="brand" loading={submitting} className="flex-1 rounded-lg text-sm font-semibold">
					Save schedule
				</Button>
				<Button type="button" variant="destructive" loading={cancelMutation.isPending} onClick={() => { void cancelMutation.mutateAsync(); }} className="flex-1 rounded-lg text-sm font-semibold">
					Cancel schedule
				</Button>
			</div>);
    }
    return (<Button type="submit" variant="brand" loading={submitting} className="w-full rounded-lg text-sm font-semibold">
			{giveaway ? 'Create schedule' : 'Start giveaway'}
		</Button>);
});
